//@ts-check
import {isEmpty, juxt, pipe, prop, tap} from 'ramda';

import {fetchJsonV1, fetchJsonV2} from '@sinclair-digital/v2-content-service/lib/fetch';

import {
  SEARCH_RESULT_LIMIT,
  getSearchParametersV1,
  getSearchParametersV2,
  searchTextExpression,
  dateExpression,
  packageTypeExpression,
  durationExpression,
  orderByExpression,
  buildV2Parameters,
} from './queries';

const API_ROOT = '/v2';
const CHUNK_SIZE = 50;

export const fetchUuids = ({searchUrl, query}) => {
  const opts = {
    method: 'POST',
    body: JSON.stringify(getSearchParametersV1(query)),
  };

  return fetchJsonV1(searchUrl, opts);
};

export function* searchResultsGeneratorV1({query, searchUrl, summaryUrl, chunkSize = CHUNK_SIZE}) {
  let uuids = null;
  let currentChunk = 0;
  const setUuids = _uuids => uuids = _uuids;

  while (uuids == null) {
    yield fetchUuids({searchUrl, summaryUrl, query})
      .then(setUuids)
      .then(() => []);
  }

  if (uuids.length) {
    while ((currentChunk * chunkSize) < uuids.length) {
      const startIndex = currentChunk * chunkSize;
      const endIndex = startIndex + chunkSize;
      const currentUuids = uuids.slice(startIndex, endIndex);
      yield fetchJsonV1(summaryUrl, {
        method: 'POST',
        body: JSON.stringify(currentUuids),
      });

      currentChunk = currentChunk + 1;
    }
  } else {
    yield Promise.resolve([]);
  }
}

export function* searchResultsGeneratorV2({
  query,
  searchUrl = `${API_ROOT}/api/content/search`,
  chunkSize = CHUNK_SIZE,
  ...options
}) {
  let currentOffset = 0;
  let finished = false;
  const isFinished = tap(results => finished = isEmpty(results));

  while (currentOffset < SEARCH_RESULT_LIMIT && !finished) {
    yield fetchJsonV2(searchUrl, {
      method: 'POST',
      body: JSON.stringify(getSearchParametersV2(query, currentOffset, chunkSize, options)),
    }).then(isFinished);

    currentOffset = currentOffset + chunkSize;
  }
}

const setParameter = ({parameters}, parameter) => val =>
  new QueryBuilder({
    ...parameters,
    [parameter]: val,
  });

export class QueryBuilder {
  constructor(parameters = {}) {
    this.parameters = parameters;
  }

  withDurationLessThan = setParameter(this, 'maxDuration');
  withDurationMoreThan = setParameter(this, 'minDuration');
  withFromDate = setParameter(this, 'fromDate');
  withThruDate = setParameter(this, 'thruDate');
  withPackageTypes = setParameter(this, 'packageTypes');
  withSearchText = setParameter(this, 'searchText');
  withSortColumn = setParameter(this, 'sortColumn');
  withSortDirection = setParameter(this, 'sortDirection');
  withOffset = setParameter(this, 'offset');
  withLimit = setParameter(this, 'limit');

  toParameters = ({offset = this.parameters.offset, limit = this.parameters.limit} = {}) =>
    buildV2Parameters({
      expressions: juxt([
        searchTextExpression,
        dateExpression,
        pipe(prop('packageTypes'), packageTypeExpression),
        durationExpression,
      ])(this.parameters),
      orderBy: orderByExpression(this.parameters),
      offset,
      limit,
    });

  toResultGenerator = function* ({
    searchUrl = `${API_ROOT}/api/content/search`,
    chunkSize = CHUNK_SIZE,
  } = {}) {
    let currentOffset = 0;
    let finished = false;
    const isFinished = tap(results => finished = isEmpty(results));

    while (currentOffset < SEARCH_RESULT_LIMIT && !finished) {
      yield fetchJsonV2(searchUrl, {
        method: 'POST',
        body: JSON.stringify(this.toParameters({offset: currentOffset, limit: SEARCH_RESULT_LIMIT})),
      }).then(isFinished);

      currentOffset = currentOffset + chunkSize;
    }
  }
}

export const buildQuery = searchText => new QueryBuilder({searchText});
